/**
 * Feja Landing Page
 *
 * COPY — edit all text here in the COPY object at the top.
 * DESIGN — edit components below if you need layout changes.
 */

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  ZapIcon, ShieldCheckIcon, FileCheckIcon, CheckCircle2Icon,
  CheckIcon, XIcon, ThermometerIcon, AlertTriangleIcon, SnowflakeIcon, FlameIcon,
} from 'lucide-react'
import supabase from './supabase'

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY

// ─────────────────────────────────────────────
// PHONE MOCKUP
// ─────────────────────────────────────────────
function PhoneMockup() {
  return (
    <div
      className="relative"
      style={{
        width: '288px',
        height: '594px',
        transform: 'rotate(-6deg)',
        filter: 'drop-shadow(0 40px 60px rgba(20, 16, 14, 0.25)) drop-shadow(0 20px 30px rgba(232, 64, 26, 0.12))',
      }}
    >
      <div className="absolute inset-0 rounded-[50px] bg-gradient-to-br from-[#1c1c1e] via-[#0f0f10] to-[#1c1c1e] p-[3px]">
        <div className="absolute inset-y-12 left-0 w-[2px] rounded-full bg-white/10" />
        <div className="absolute inset-y-12 right-0 w-[2px] rounded-full bg-white/5" />
        <div className="relative h-full w-full rounded-[47px] bg-black p-[8px]">
          <div className="relative h-full w-full overflow-hidden rounded-[40px] bg-[#FAF7F5]">
            <div className="flex items-center justify-between px-7 pt-3 pb-1 text-[11px] font-semibold text-neutral-900">
              <span>9:41</span>
              <div className="flex items-center gap-1">
                <svg width="16" height="10" viewBox="0 0 16 10" fill="none">
                  <rect x="0" y="6" width="3" height="4" rx="0.5" fill="currentColor" />
                  <rect x="4.5" y="4" width="3" height="6" rx="0.5" fill="currentColor" />
                  <rect x="9" y="2" width="3" height="8" rx="0.5" fill="currentColor" />
                  <rect x="13.5" y="0" width="3" height="10" rx="0.5" fill="currentColor" />
                </svg>
                <svg width="24" height="11" viewBox="0 0 24 11" fill="none">
                  <rect x="0.5" y="0.5" width="20" height="10" rx="2.5" stroke="currentColor" fill="none" />
                  <rect x="2" y="2" width="17" height="7" rx="1.5" fill="currentColor" />
                  <rect x="21.5" y="3.5" width="1.5" height="4" rx="0.5" fill="currentColor" />
                </svg>
              </div>
            </div>
            <div className="absolute top-2.5 left-1/2 -translate-x-1/2 h-[26px] w-[100px] rounded-full bg-black" />
            <div className="px-6 pt-8 pb-4 flex items-start justify-between">
              <div>
                <h1 className="text-[22px] font-bold tracking-tight text-neutral-900 leading-tight">Today's Checklist</h1>
                <p className="text-[12px] text-neutral-500 mt-0.5">Monday, 18 May · Morning shift</p>
              </div>
              <div className="h-8 w-8 rounded-lg bg-[#E8401A] flex items-center justify-center">
                <span className="text-white text-[12px] font-bold leading-none tracking-tight">FJ</span>
              </div>
            </div>
            <div className="px-6 pb-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-medium text-neutral-500 uppercase tracking-wide">Daily progress</span>
                <span className="text-[11px] font-semibold text-neutral-900">4 of 5</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-neutral-200/70 overflow-hidden">
                <div className="h-full w-[80%] rounded-full bg-[#E8401A]" />
              </div>
            </div>
            <div className="px-4 space-y-3">
              <div className="rounded-2xl bg-white border border-neutral-200/80 p-4 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-xl bg-sky-50 flex items-center justify-center">
                      <SnowflakeIcon className="h-4 w-4 text-sky-600" strokeWidth={2.5} />
                    </div>
                    <div>
                      <h3 className="text-[15px] font-bold text-neutral-900 leading-tight">Fridge 1</h3>
                      <p className="text-[11px] text-neutral-500">Cold · max 5°C</p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 border border-emerald-100">
                    <CheckIcon className="h-2.5 w-2.5" strokeWidth={3.5} />3/3 logged
                  </span>
                </div>
                <div className="space-y-1.5">
                  {[{ name: 'Dairy', temp: '3°C' }, { name: 'Meat', temp: '4°C' }, { name: 'Seafood', temp: '2°C' }].map((item) => (
                    <div key={item.name} className="flex items-center justify-between rounded-lg bg-neutral-50/70 px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="h-5 w-5 rounded-full bg-emerald-500 flex items-center justify-center">
                          <CheckIcon className="h-3 w-3 text-white" strokeWidth={3.5} />
                        </div>
                        <span className="text-[13px] font-medium text-neutral-800">{item.name}</span>
                      </div>
                      <span className="text-[13px] font-semibold text-neutral-900 tabular-nums">{item.temp}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl bg-white border border-[#E8401A]/30 p-4 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1 bg-[#E8401A]" />
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-xl bg-orange-50 flex items-center justify-center">
                      <FlameIcon className="h-4 w-4 text-[#E8401A]" strokeWidth={2.5} />
                    </div>
                    <div>
                      <h3 className="text-[15px] font-bold text-neutral-900 leading-tight">Hot Hold</h3>
                      <p className="text-[11px] text-neutral-500">Hot · min 60°C</p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#E8401A] px-2 py-0.5 text-[10px] font-semibold text-white">
                    <AlertTriangleIcon className="h-2.5 w-2.5" strokeWidth={3} />Action required
                  </span>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between rounded-lg bg-red-50 border border-red-100 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="h-5 w-5 rounded-full bg-red-500 flex items-center justify-center">
                        <XIcon className="h-3 w-3 text-white" strokeWidth={3.5} />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[13px] font-medium text-neutral-900 leading-tight">Soup</span>
                        <span className="text-[10px] font-semibold text-red-600 leading-tight mt-0.5">Temp too low</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <ThermometerIcon className="h-3 w-3 text-red-500" strokeWidth={2.5} />
                      <span className="text-[13px] font-bold text-red-600 tabular-nums">52°C</span>
                    </div>
                  </div>
                </div>
                <button className="mt-3 w-full rounded-lg bg-neutral-900 text-white text-[12px] font-semibold py-2">
                  Log corrective action
                </button>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#FAF7F5] via-[#FAF7F5]/80 to-transparent pointer-events-none" />
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 h-1 w-32 rounded-full bg-neutral-900/80" />
          </div>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// COPY — edit all text here
// ─────────────────────────────────────────────
const COPY = {
  nav: {
    logo: 'Feja',
    signIn: 'Sign in',
  },
  hero: {
    headline: 'Your Food Safety Program, done.',
    subheadline: 'Everything your Food Safety Program requires, done daily in seconds.',
    cta: 'Join the waitlist',
    sub: 'Built for Australian hospitality',
  },
  problem: {
    body: "Feja logs temperatures, tracks corrective actions, manages your team and keeps your records inspection-ready. All from any phone, every single day.",
  },
  pillars: [
    {
      title: 'Done in seconds',
      description:
        'Staff log temperatures from their phone. No paper, no clipboards, no chasing. HACCP-compliant records in under a minute.',
    },
    {
      title: 'Always ready',
      description:
        'Every temperature check, every corrective action, every timestamp stored and exportable. Hand records to an inspector instantly.',
    },
    {
      title: 'Proof when it matters',
      description:
        "If something goes wrong, Feja shows you did everything right. Timestamped logs and corrective actions on record mean you're covered.",
    },
  ],
  howItWorks: {
    heading: 'How it works',
    steps: [
      {
        number: '01',
        title: 'Set up your venue',
        description:
          'Feja pre-fills your sections and thresholds based on Australian food safety standards.',
      },
      {
        number: '02',
        title: 'Staff log checks daily',
        description:
          'Your team logs temps in seconds. Feja flags anything outside safe limits instantly.',
      },
      {
        number: '03',
        title: 'You stay in control',
        description:
          'Get alerted when something fails. Export records for inspections in one tap.',
      },
    ],
  },
  leadForm: {
    heading: 'Get early access',
    body: "Feja is in early access. Enter your details and we'll be in touch.",
    fields: {
      name: 'Your name',
      venue: 'Venue name',
      email: 'Email address',
      type: 'Venue type',
    },
    venueTypes: [
      { value: 'restaurant', label: 'Restaurant/Cafe' },
      { value: 'bar', label: 'Bar/Pub' },
      { value: 'hotel', label: 'Hotel' },
      { value: 'catering', label: 'Catering' },
      { value: 'other', label: 'Other' },
    ],
    button: 'Join the waitlist',
    disclaimer: 'Your details are kept secure. By joining, you agree to our Privacy Policy.',
    successHeading: 'Request received',
    successBody:
      "Thanks for your interest in Feja. We'll be in touch shortly with your early access details.",
  },
  footer: {
    tagline: 'Food safety compliance for Australian hospitality venues.',
    sub: 'Built in Australia.',
  },
}

// ─────────────────────────────────────────────
// COMPONENTS
// ─────────────────────────────────────────────

const LOGO_URL = 'https://res.cloudinary.com/dqzdn543t/image/upload/v1778932146/feja-logo-light-removebg-preview_1_lzt9vu.png'

function Nav({ onSignIn }) {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#FAF7F2]/90 md:bg-[#FAF7F2]/75 backdrop-blur-md border-b border-[#E5E2DC]/50">
      <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-center md:justify-between relative">
        <img src={LOGO_URL} alt="Feja" style={{ height: '86px', width: 'auto' }} />
        <div className="absolute right-6 md:static flex items-center gap-6">
          {onSignIn && (
            <span
              onClick={onSignIn}
              className="text-sm text-[#5C5A58] hover:text-[#1A1A1A] transition-colors cursor-pointer"
            >
              {COPY.nav.signIn}
            </span>
          )}
        </div>
      </div>
    </header>
  )
}

function Hero() {
  return (
    <section className="pt-36 pb-8 lg:pb-16 px-6 max-w-6xl mx-auto lg:min-h-[90vh] flex flex-col lg:flex-row items-center lg:gap-16">
      <div className="flex-1 text-center lg:text-left">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-5xl lg:text-7xl font-bold tracking-tight text-[#1A1A1A] leading-[1.1] mb-6"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        >
          <span className="md:hidden">Your Food<br />Safety Program<br /></span>
          <span className="hidden md:inline">Your Food Safety<br />Program,<br /></span>
          Done <span style={{ color: '#E8401A' }}>&#10004;</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-xl font-medium text-[#5C5A58] mb-10 max-w-2xl mx-auto lg:mx-0"
        >
          {COPY.hero.subheadline}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <a
            href="#early-access"
            className="inline-flex items-center justify-center px-8 py-4 bg-[#E8401A] hover:bg-[#D13917] text-white font-medium rounded-xl transition-colors text-lg no-underline"
            style={{ textDecoration: 'none' }}
          >
            {COPY.hero.cta}
          </a>
          <p className="mt-4 text-sm text-[#5C5A58]">{COPY.hero.sub}</p>
        </motion.div>
      </div>
      <div className="hidden lg:flex flex-1 items-center justify-center">
        <PhoneMockup />
      </div>
    </section>
  )
}

function Problem() {
  return (
    <section className="pt-6 pb-12 md:py-24 px-6">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
        >
          <div className="w-16 h-1 bg-[#E8401A] mx-auto mb-10 rounded-full" />
          <p className="text-2xl md:text-4xl font-medium text-[#1A1A1A] leading-snug">
            {COPY.problem.body}
          </p>
        </motion.div>
      </div>
    </section>
  )
}

const pillarIcons = [
  <ZapIcon size={24} />,
  <ShieldCheckIcon size={24} />,
  <FileCheckIcon size={24} />,
]

function Pillars() {
  return (
    <section className="pt-6 pb-12 md:py-24 px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12 lg:gap-16">
          {COPY.pillars.map((pillar, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`flex flex-col ${index === 1 ? 'items-end text-right md:items-start md:text-left' : 'items-start'}`}
            >
              <div className="w-12 h-12 rounded-2xl bg-[#E8401A]/10 text-[#E8401A] flex items-center justify-center mb-6">
                {pillarIcons[index]}
              </div>
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-4">{pillar.title}</h3>
              <p className="text-[#5C5A58] leading-relaxed">{pillar.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

function HowItWorks() {
  return (
    <section className="pt-6 pb-12 md:py-32 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
          className="text-4xl font-bold text-[#1A1A1A] mb-16 text-center"
        >
          {COPY.howItWorks.heading}
        </motion.h2>
        <div className="space-y-12">
          {COPY.howItWorks.steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="flex flex-col md:flex-row gap-6 md:gap-10 items-start md:items-center"
            >
              <div className="w-20 md:w-28 text-6xl md:text-7xl font-bold text-[#E8401A]/20 leading-none shrink-0 text-center">
                {step.number}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-[#1A1A1A] mb-3">{step.title}</h3>
                <p className="text-lg text-[#5C5A58] leading-relaxed">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

function LeadForm() {
  const [name, setName] = useState('')
  const [venue, setVenue] = useState('')
  const [email, setEmail] = useState('')
  const [venueType, setVenueType] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    const { error: dbError } = await supabase.from('leads').insert({
      contact_name: name.trim(),
      email: email.trim(),
      venue_name: venue.trim(),
      venue_type: venueType,
    })

    if (dbError) {
      setError('Something went wrong. Please try again.')
      setLoading(false)
      return
    }

    fetch(`${SUPABASE_URL}/functions/v1/notify-lead`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        record: {
          contact_name: name.trim(),
          email: email.trim(),
          venue_name: venue.trim(),
          venue_type: venueType,
        },
      }),
    }).catch(() => {})

    setLoading(false)
    setIsSubmitted(true)
  }

  return (
    <section id="early-access" className="pt-6 pb-12 md:py-24 px-6 bg-white">
      <div className="max-w-xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="bg-[#FAF7F2] rounded-3xl p-8 md:p-12 shadow-xl shadow-black/5 border border-[#E5E2DC]/50"
        >
          {!isSubmitted ? (
            <>
              <div className="text-center mb-10">
                <h3 className="text-2xl font-bold text-[#1A1A1A] mb-4">Join the Waitlist</h3>
                <p className="text-[#5C5A58]">{COPY.leadForm.body}</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="lp-name" className="block text-sm font-medium text-[#1A1A1A] mb-1.5" style={{ paddingLeft: '10px' }}>
                    {COPY.leadForm.fields.name}
                  </label>
                  <input
                    type="text"
                    id="lp-name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#E5E2DC] bg-white focus:outline-none focus:ring-2 focus:ring-[#E8401A]/50 focus:border-[#E8401A] transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="lp-venue" className="block text-sm font-medium text-[#1A1A1A] mb-1.5" style={{ paddingLeft: '10px' }}>
                    {COPY.leadForm.fields.venue}
                  </label>
                  <input
                    type="text"
                    id="lp-venue"
                    required
                    value={venue}
                    onChange={(e) => setVenue(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#E5E2DC] bg-white focus:outline-none focus:ring-2 focus:ring-[#E8401A]/50 focus:border-[#E8401A] transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="lp-email" className="block text-sm font-medium text-[#1A1A1A] mb-1.5" style={{ paddingLeft: '10px' }}>
                    {COPY.leadForm.fields.email}
                  </label>
                  <input
                    type="email"
                    id="lp-email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#E5E2DC] bg-white focus:outline-none focus:ring-2 focus:ring-[#E8401A]/50 focus:border-[#E8401A] transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="lp-type" className="block text-sm font-medium text-[#1A1A1A] mb-1.5" style={{ paddingLeft: '10px' }}>
                    {COPY.leadForm.fields.type}
                  </label>
                  <select
                    id="lp-type"
                    required
                    value={venueType}
                    onChange={(e) => setVenueType(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[#E5E2DC] bg-white focus:outline-none focus:ring-2 focus:ring-[#E8401A]/50 focus:border-[#E8401A] transition-colors appearance-none"
                  >
                    <option value="">Select venue type</option>
                    {COPY.leadForm.venueTypes.map((vt) => (
                      <option key={vt.value} value={vt.value}>
                        {vt.label}
                      </option>
                    ))}
                  </select>
                </div>
                {error && (
                  <p className="text-sm text-red-600">{error}</p>
                )}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 bg-[#E8401A] hover:bg-[#D13917] disabled:opacity-60 text-white font-medium rounded-xl transition-colors text-lg"
                  >
                    {loading ? 'Sending...' : COPY.leadForm.button}
                  </button>
                  <p className="text-center text-xs text-[#5C5A58]/70 mt-4">
                    Your details are kept secure. By joining, you agree to our{' '}
                    <a href="/privacy" className="underline hover:text-[#1A1A1A] transition-colors">Privacy Policy</a>.
                  </p>
                </div>
              </form>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-12"
            >
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2Icon size={32} />
              </div>
              <h3 className="text-2xl font-bold text-[#1A1A1A] mb-3">
                {COPY.leadForm.successHeading}
              </h3>
              <p className="text-[#5C5A58]">{COPY.leadForm.successBody}</p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer className="pt-6 pb-12 md:py-12 px-6 border-t border-[#E5E2DC]/50">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center md:items-center gap-6 mb-8">
          <img src={LOGO_URL} alt="Feja" style={{ height: '76px', width: 'auto' }} />
          <div className="text-center md:text-right">
            <p className="text-[#5C5A58] text-sm mb-1">{COPY.footer.tagline}</p>
            <p className="text-[#5C5A58]/70 text-sm">{COPY.footer.sub}</p>
          </div>
        </div>
        <div className="border-t border-[#E5E2DC]/50 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-[#5C5A58]/60">&copy; {new Date().getFullYear()} Feja. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <a href="/terms" className="text-xs text-[#5C5A58] hover:text-[#1A1A1A] transition-colors" style={{ textDecoration: 'none' }}>Terms</a>
            <a href="/privacy" className="text-xs text-[#5C5A58] hover:text-[#1A1A1A] transition-colors" style={{ textDecoration: 'none' }}>Privacy</a>
            <a href="/refunds" className="text-xs text-[#5C5A58] hover:text-[#1A1A1A] transition-colors" style={{ textDecoration: 'none' }}>Refunds</a>
          </div>
        </div>
      </div>
    </footer>
  )
}

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────
export default function LandingPage({ onSignIn }) {
  return (
    <div className="min-h-screen bg-[#FAF7F2] font-sans text-[#1A1A1A]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      <Nav onSignIn={onSignIn} />
      <main>
        <Hero />
        <Problem />
        <Pillars />
        <HowItWorks />
        <LeadForm />
      </main>
      <Footer />
    </div>
  )
}
